import { JWTPayload, decodeJwt } from "jose";
import { GailProductRoles } from "@/enums/user";
import { TOKEN } from "@/types/common/ids";
import { IsSuperAdmin } from "./admin";
import { BASE_GAIL } from "./baseUrls";

const TOKEN_HOST = process.env.NEXT_PUBLIC_AUTH_TOKEN_HOST!;

export type ProductRolesState = {
  roles: GailProductRoles[] | undefined;
  loaded: boolean;
};

const decodeToken = (accessToken: TOKEN): JWTPayload => {
  if (!accessToken) {
    return {};
  }

  return decodeJwt(accessToken);
};

export const IsPrivileged = (accessToken: TOKEN): boolean => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload.email &&
    typeof tokenPayload.email === "string" &&
    IsSuperAdmin({ email: tokenPayload.email as string })
  ) {
    return true;
  }

  return false;
};

/**
 * Gets the internal organization/agency ID from the access token.
 * This reads from the namespaced ${TOKEN_HOST}/org_id claim.
 *
 * Note: This is the internal org/agency ID, not the Auth0 Organization ID.
 * For the Auth0 Organization ID, use GetOrgId().
 */
export const GetAgencyId = (accessToken: TOKEN): string | null => {
  const tokenPayload = decodeToken(accessToken);

  // Read from namespaced org_id claim (internal org/agency ID)
  const orgId = tokenPayload[`${TOKEN_HOST}/org_id`];
  if (orgId && typeof orgId === "string") {
    return orgId;
  }

  return null;
};

export const GetOrgId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload?.org_id && typeof tokenPayload.org_id === "string") {
    return tokenPayload.org_id;
  }

  return "";
};

/**
 * @deprecated This function previously looked up org name from user_orgs in the token.
 * Since user_orgs is no longer stored in tokens, this now returns an empty string.
 * Use the org context or API to get organization display names.
 */
export const GetOrgName = (accessToken: TOKEN): string => {
  // user_orgs is no longer available in the token
  // Return empty string - callers should use org context or API for display names
  return "";
};

export const GetEmail = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.email && typeof tokenPayload.email === "string") {
    return tokenPayload.email;
  }

  return "";
};

export const GetFirstName = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.given_name && typeof tokenPayload.given_name === "string") {
    return tokenPayload.given_name;
  }

  return "";
};

export const GetLastName = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.family_name && typeof tokenPayload.family_name === "string") {
    return tokenPayload.family_name;
  }

  return "";
};

export const GetUserId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (tokenPayload && tokenPayload.sub && typeof tokenPayload.sub === "string") {
    return tokenPayload.sub;
  }

  return "";
};

export const GetLulaUserId = (accessToken: TOKEN): string => {
  const tokenPayload = decodeToken(accessToken);

  if (
    tokenPayload &&
    tokenPayload[`${TOKEN_HOST}/user_id`] &&
    typeof tokenPayload[`${TOKEN_HOST}/user_id`] === "string"
  ) {
    return tokenPayload[`${TOKEN_HOST}/user_id`] as string;
  }

  return "";
};

export const CalUserRefreshEndpoint = (accessToken: TOKEN) => {
  const orgId = GetAgencyId(accessToken);
  const auth0UserId = GetUserId(accessToken);

  return `${BASE_GAIL}/api/calendar/refresh/${orgId}/${auth0UserId}`;
};

/**
 * Compares two arrays of roles for equality.
 * Order doesn't matter - arrays are sorted before comparison.
 *
 * @param roles1 First array of roles to compare
 * @param roles2 Second array of roles to compare
 * @returns true if the arrays contain the same roles, false otherwise
 */
export const areRolesEqual = (
  roles1: GailProductRoles[] | undefined,
  roles2: GailProductRoles[] | undefined
): boolean => {
  if (roles1 === roles2) return true;
  if (!roles1 || !roles2) return false;
  if (roles1.length !== roles2.length) return false;

  // Sort both arrays for comparison (order shouldn't matter for roles)
  const sorted1 = [...roles1].sort();
  const sorted2 = [...roles2].sort();

  return sorted1.every((role, index) => role === sorted2[index]);
};

/**
 * Compares two ProductRolesState objects for equality.
 *
 * @param state1 First ProductRolesState to compare
 * @param state2 Second ProductRolesState to compare
 * @returns true if both states have the same loaded status and roles, false otherwise
 */
export const areProductRolesStatesEqual = (
  state1: ProductRolesState,
  state2: ProductRolesState
): boolean => {
  return state1.loaded === state2.loaded && areRolesEqual(state1.roles, state2.roles);
};
